import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#9CA3AF";
const TEXT_DIM = "#6B7280";

export default function SalesforceDocs() {
  const navigate = useNavigate();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch("/SALESFORCE_QUICKSTART.md")
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load guide");
        return r.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  return (
    <div
      data-testid="salesforce-docs-page"
      style={{
        minHeight: "100vh",
        background: BG,
        color: TEXT_PRIMARY,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        .sf-doc h1 { font-size: 32px; font-weight: 700; letter-spacing: -0.02em; margin: 32px 0 16px; color: ${TEXT_PRIMARY}; }
        .sf-doc h2 { font-size: 22px; font-weight: 600; letter-spacing: -0.01em; margin: 36px 0 14px; color: ${TEXT_PRIMARY}; padding-top: 12px; border-top: 1px solid ${BORDER}; }
        .sf-doc h2:first-of-type { border-top: none; padding-top: 0; }
        .sf-doc h3 { font-size: 17px; font-weight: 600; margin: 24px 0 10px; color: ${TEXT_PRIMARY}; }
        .sf-doc p { font-size: 14px; line-height: 1.7; color: ${TEXT_MUTED}; margin: 12px 0; }
        .sf-doc ul, .sf-doc ol { padding-left: 22px; margin: 12px 0; color: ${TEXT_MUTED}; }
        .sf-doc li { font-size: 14px; line-height: 1.7; margin: 4px 0; }
        .sf-doc strong { color: ${TEXT_PRIMARY}; font-weight: 600; }
        .sf-doc a { color: ${ACCENT}; text-decoration: none; border-bottom: 1px solid ${ACCENT}55; }
        .sf-doc a:hover { border-bottom-color: ${ACCENT}; }
        .sf-doc code { font-family: 'DM Mono', monospace; font-size: 12.5px; background: ${SURFACE_2}; padding: 2px 6px; border-radius: 4px; color: #93C5FD; border: 1px solid ${BORDER}; }
        .sf-doc pre { background: #05070C; border: 1px solid ${BORDER}; border-radius: 8px; padding: 18px; overflow-x: auto; margin: 16px 0; }
        .sf-doc pre code { background: transparent; border: none; padding: 0; color: #E5E7EB; font-size: 12.5px; line-height: 1.65; }
        .sf-doc blockquote { border-left: 3px solid ${ACCENT}; padding: 8px 16px; margin: 16px 0; background: ${ACCENT}0D; border-radius: 0 6px 6px 0; }
        .sf-doc blockquote p { color: ${TEXT_PRIMARY}; margin: 0; }
        .sf-doc table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 13px; }
        .sf-doc th { background: ${SURFACE_2}; padding: 10px 14px; text-align: left; font-weight: 600; color: ${TEXT_PRIMARY}; border: 1px solid ${BORDER}; font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; }
        .sf-doc td { padding: 10px 14px; border: 1px solid ${BORDER}; color: ${TEXT_MUTED}; font-size: 13px; vertical-align: top; }
        .sf-doc td code { font-size: 11.5px; }
        .sf-doc hr { border: none; border-top: 1px solid ${BORDER}; margin: 32px 0; }
      `}</style>

      {/* Top bar */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: `${BG}EE`,
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <div
          style={{
            maxWidth: 920,
            margin: "0 auto",
            padding: "16px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <button
            data-testid="back-to-app-btn"
            onClick={() => navigate("/app")}
            style={{
              background: "transparent",
              border: `1px solid ${BORDER}`,
              color: TEXT_MUTED,
              padding: "7px 14px",
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = ACCENT)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = BORDER)}
          >
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to app
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: ACCENT,
                fontFamily: "'DM Mono', monospace",
              }}
            >
              Salesforce Quickstart
            </span>
          </div>

          <a
            href="/api/docs"
            target="_blank"
            rel="noreferrer"
            data-testid="api-reference-link"
            style={{
              background: ACCENT,
              color: "#fff",
              padding: "7px 14px",
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 600,
              textDecoration: "none",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            API Reference →
          </a>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 28px 80px" }}>
        {loading && (
          <div data-testid="docs-loading" style={{ padding: "80px 0", textAlign: "center", color: TEXT_MUTED, fontSize: 13 }}>
            Loading guide…
          </div>
        )}
        {error && (
          <div
            data-testid="docs-error"
            style={{
              padding: 24,
              marginTop: 32,
              background: `${SURFACE}`,
              border: `1px solid #7F1D1D`,
              borderRadius: 8,
              color: "#FCA5A5",
              fontSize: 13,
            }}
          >
            Failed to load the Salesforce Quickstart guide. {error}
          </div>
        )}
        {!loading && !error && (
          <article className="sf-doc" data-testid="docs-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </article>
        )}
      </div>
    </div>
  );
}
